import { SelectionRangeModel } from "../../Utils/Models";
import styles from "../../Styles/task.module.css";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { useContext, useEffect, useState } from "react";
import { Vocabulary } from "../../Utils/Vocabulary";
import { format, parseISO, differenceInDays } from "date-fns";
import { ro } from "date-fns/locale";
import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from "@mui/material";
import Config from "../../Utils/Config";
import { CustomThemeContext } from "../../Context/CustomThemeProvider";
import { DateRangePicker } from "react-date-range";
import { Controller } from "react-hook-form";
import { TaskPeriodicityProps } from "../../Utils/Types";

export default function TaskPeriodicity(props: TaskPeriodicityProps) {
  const {
    control,
    setValue,
    periodicities,
    setOpenPeriodicity,
    getValues,
    watch,
  } = props;
  const themeContext: any = useContext(CustomThemeContext);
  const taskSelectionDate = {
    startDate: getValues("start") ? parseISO(getValues("start")) : new Date(),
    endDate: getValues("end") ? parseISO(getValues("end")) : new Date(),
    key: "selection",
  };
  const [selectionRange, setSelectionRange] = useState(
    getValues("_id") ? taskSelectionDate : new SelectionRangeModel()
  );
  /**
   *
   * @param ranges
   */
  const handleSelect = (ranges: any) => {
    setSelectionRange(ranges.selection);
    setValue(
      "start",
      format(ranges.selection.startDate, Config.rangeDatePickerFormat)
    );
    setValue(
      "end",
      format(ranges.selection.endDate, Config.rangeDatePickerFormat)
    );
  };

  /**
   *reset component to default values
   */
  const handleReset = () => {
    setSelectionRange(new SelectionRangeModel());
    setValue("periodicity", null);
  };

  /**
   * Hide the left container from the date range picker
   */
  useEffect(() => {
    const leftContainer = document.getElementsByClassName(
      "rdrDefinedRangesWrapper"
    );
    leftContainer[0].setAttribute("style", "display: none");
  }, []);

  /**
   *
   * @returns
   */
  const renderBottomOptions = () => {
    return (
      <>
        <Divider variant="middle" />
        <div className={styles.optionsContainer}>
          <Button onClick={handleReset} variant="contained">
            {Vocabulary.reset}
          </Button>
          <Button variant="contained" onClick={handleSubmit} color="secondary">
            {Vocabulary.ok}
          </Button>
        </div>
      </>
    );
  };

  /**
   *
   * @returns
   */
  const renderSkipWeekendsComponent = () => {
    return watch("periodicity")?.name === Vocabulary.daily ? (
      <>
        <div className={styles.periodicityContainer}>
          <FormGroup>
            <Controller
              name="skipWeekend"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  checked={value}
                  onChange={onChange}
                  control={<Checkbox color="primary" />}
                  label={Vocabulary.skipWeekend}
                />
              )}
            />
          </FormGroup>
        </div>
      </>
    ) : null;
  };

  /**
   *
   * @returns
   */
  const renderCustomFilters = () => {
    return (
      <>
        <Divider variant="middle" />
        <div className={styles.periodicityContainer}>
          <Typography>{Vocabulary.repeatsOn}:</Typography>
          <Controller
            name="periodicity"
            control={control}
            defaultValue={undefined}
            render={({ field: { ref, ...field } }) => (
              <Autocomplete
                noOptionsText={Vocabulary.noResultsFound}
                id="periodicity"
                className={styles.taskAutocomplete}
                disablePortal
                isOptionEqualToValue={(option: any, value: any) =>
                  option.id === value.id
                }
                getOptionLabel={(option: any) =>
                  option?.name ? option?.name : ""
                }
                options={periodicities || []}
                value={
                  getValues("periodicity") ? getValues("periodicity") : null
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                  />
                )}
                onChange={(_, value) => field.onChange(value)}
              />
            )}
          />
        </div>
      </>
    );
  };

  /**
   * Create a new periodicity object and assign it to the task
   */
  const handleSubmit = () => {
    if (selectionRange.startDate && selectionRange.endDate) {
      const selectedPeriodicity = getValues("periodicity");

      // Calculate number of days between start and end date
      const nrOfDays = differenceInDays(
        selectionRange.endDate,
        selectionRange.startDate
      ) + 1; // Add 1 to include both start and end days

      let newPeriodicity: {
        _id?: string | null;
        name: string;
        tag: string;
        nrOfDays: number;
      };

      // Create new periodicity object with selected values and calculated days
      if (selectedPeriodicity && selectedPeriodicity.name && selectedPeriodicity.tag) {
        newPeriodicity = {
          _id: selectedPeriodicity._id || null,
          name: selectedPeriodicity.name,
          tag: selectedPeriodicity.tag,
          nrOfDays: nrOfDays,
        };
      } else {
        // If no periodicity selected, use default periodic values
        newPeriodicity = {
          name: `periodic-${nrOfDays}`,
          tag: `periodic-${nrOfDays}`,
          nrOfDays: nrOfDays,
        };
      }

      setValue("periodicity", newPeriodicity, { shouldValidate: true });
      setOpenPeriodicity(false);
    }
  };

  return (
    <>
      <DateRangePicker
        locale={ro}
        ranges={[selectionRange]}
        onChange={handleSelect}
        dateDisplayFormat={Config.momentEUDateFormatForDateRange}
        staticRanges={[]}
        inputRanges={[]}
        editableDateInputs={true}
        startDatePlaceholder={Vocabulary.startDate}
        endDatePlaceholder={Vocabulary.endDate}
        rangeColors={[themeContext.theme?.palette.secondary.main]}
      />
      {renderCustomFilters()}
      {renderSkipWeekendsComponent()}
      {renderBottomOptions()}
    </>
  );
}
